import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import { createColumnHelper } from '@tanstack/react-table'
import { generatePath, useNavigate } from 'react-router-dom'

import { useGetSbpPaymentsByMethods2C } from '@/services'
import { TableComponent } from '@/components/table'
import { APP_ROUTES } from '@/constants.ts'
import { FetchErrorMessage } from '@/components'
import { DefaultFiltersState } from '@/stores'
import { useIsMobile } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/stores/hooks.ts'
import { userSelectors } from '@/stores/slices/user-slice.ts'

import {
  defaultSettings,
  getColumnsSettings,
  KEY_PAGE,
  PartialPaymentsRequest,
} from '@/pages/b2c/default-settings-payments.tsx'
import { B2CPaymentsRequest } from '@/@types/b2c.types.ts'
import { sbpPaymentsActions, sbpPaymentSelectors } from '@/stores/slices/sbp-payments-slice.ts'

const columnHelper = createColumnHelper<B2CPaymentsRequest>()
const defaultColumns = getColumnsSettings(columnHelper)

export const ToCPaymentsTable = (): React.ReactElement => {
  const { isLoading, data, isError, refetch, isFetching } = useGetSbpPaymentsByMethods2C()
  const navigate = useNavigate()
  const settings = useAppSelector((state) => userSelectors.personalSettings(state, KEY_PAGE))
  const isMobile = useIsMobile()
  const dispatch = useAppDispatch()
  const { page, showCount } = useAppSelector(sbpPaymentSelectors.filters)

  const b2cRequestCols = useMemo(() => {
    const format = isMobile ? 'mobile' : 'desktop'

    const desktopSettings = (settings?.[format] ?? Object.keys(defaultSettings)) as Array<keyof PartialPaymentsRequest>

    const dynamicSettings = desktopSettings.map((e) => defaultColumns[e])
    return [...dynamicSettings]
  }, [settings, isMobile])

  const handleViewTransaction = (row: B2CPaymentsRequest) => () => {
    navigate(generatePath(APP_ROUTES.B2CView, { id: row.transferId.toString() }))
  }

  const handlePaginationParam = (param: keyof DefaultFiltersState) => (value: number) =>
    dispatch(sbpPaymentsActions.setFilter({ filterKey: param, filterValue: value }))

  return (
    <FetchErrorMessage isError={isError} refetch={refetch} isFetching={isFetching}>
      <Box mt={3}>
        <TableComponent<B2CPaymentsRequest>
          rowKey="transferId"
          isLoading={isLoading || isFetching}
          columns={b2cRequestCols}
          page={page}
          size={showCount}
          totalPages={data?.data.totalPages}
          data={data?.data?.content ?? []}
          onPageChange={handlePaginationParam('page')}
          onSizeChange={handlePaginationParam('showCount')}
          onRowClick={handleViewTransaction}
        />
      </Box>
    </FetchErrorMessage>
  )
}
